import {
  CAPTURE_INTERVAL_MS, CAPTURE_VERSION, CaptureConfig, CapturedFields, CapturePayload,
  eligiblePartial, fingerprint, makePayload, normalizeFields,
  serializePayload, validEmail,
} from "@/lib/proposalPartialCapture";
import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from "@/lib/browserStorage";

export interface HubSpotFormInstance {
  getFormId(): string;
  getInstanceId(): string;
  getFormFieldValues(): Promise<unknown>;
  getConversionId(): string;
}

export interface HubSpotV4 {
  getFormFromEvent(event: Event): HubSpotFormInstance;
}

export type CaptureDiagnostic = "waiting" | "ready" | "attempted" | "completed" | "unavailable";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Browser storage holds only a session UUID and ordering counter, never field values. */
export function startProposalCapture(
  config: CaptureConfig,
  diagnostic: (state: CaptureDiagnostic) => void = () => {},
): () => void {
  let disposed = false;
  let completed = false;
  let form: HubSpotFormInstance | null = null;
  let instanceId = "";
  let current: CapturedFields | null = null;
  let latestEligible: CapturePayload | null = null;
  let sessionId: string | null = null;
  let revision = 0;
  let lastPayload: CapturePayload | null = null;
  let lastPayloadKey = "";
  let attemptedFingerprint = "";
  let flushedBody = "";
  let reportedState: CaptureDiagnostic | undefined;
  let reading = false;
  let sending = false;
  let queued: CapturePayload | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  let activeAbort: AbortController | null = null;
  let sendTimeout: ReturnType<typeof setTimeout> | undefined;
  const sessionKey = `mvo:${CAPTURE_VERSION.replace("/", ":")}:${config.environment}:${config.formId}`;

  const report = (state: CaptureDiagnostic) => {
    if (completed && state !== "completed") return;
    if (!disposed && state !== reportedState) {
      reportedState = state;
      diagnostic(state);
    }
  };

  const getSessionId = () => {
    if (sessionId) return sessionId;
    try {
      const stored = JSON.parse(getSessionStorageItem(sessionKey) || "null");
      if (stored && typeof stored.id === "string" && UUID.test(stored.id)
        && Number.isSafeInteger(stored.revision) && stored.revision >= 0 && stored.revision < Number.MAX_SAFE_INTEGER) {
        sessionId = stored.id;
        revision = stored.revision;
      }
    } catch { /* Invalid or unavailable storage starts a fresh session. */ }
    sessionId ??= window.crypto.randomUUID();
    return sessionId;
  };

  const snapshot = (fields: CapturedFields, status: CapturePayload["status"], conversionId = "") => {
    const key = JSON.stringify([status, conversionId, fingerprint(fields)]);
    if (lastPayload && key === lastPayloadKey) return lastPayload;
    const id = getSessionId();
    const payload = makePayload(config, {
      fields, sessionId: id, instanceId, status, revision: revision + 1, conversionId,
    });
    if (!payload) return null;
    revision = payload.snapshot_revision;
    setSessionStorageItem(sessionKey, JSON.stringify({ id, revision }));
    lastPayloadKey = key;
    lastPayload = payload;
    return payload;
  };

  const send = async (payload: CapturePayload): Promise<void> => {
    if (disposed || completed) return;
    if (sending) { queued = payload; return; }
    const body = serializePayload(payload);
    if (!body) return;
    attemptedFingerprint = fingerprint(payload.fields);
    sending = true;
    activeAbort = new AbortController();
    sendTimeout = setTimeout(() => activeAbort?.abort(), 10_000);
    try {
      // Opaque success means attempted, not saved. Never inspect or log a body.
      await window.fetch(config.endpoint, {
        method: "POST", mode: "no-cors", credentials: "omit", referrerPolicy: "no-referrer",
        headers: { "Content-Type": "text/plain;charset=UTF-8" }, body,
        signal: activeAbort.signal,
      });
      report("attempted");
    } catch {
      report("unavailable");
    } finally {
      clearTimeout(sendTimeout);
      activeAbort = null;
      sending = false;
      const next = queued;
      queued = null;
      if (next && !disposed && !completed) void send(next);
    }
  };

  const beacon = (payload: CapturePayload) => {
    const body = serializePayload(payload);
    if (!body || body === flushedBody) return;
    flushedBody = body;
    try {
      if (window.navigator.sendBeacon?.(config.endpoint, new Blob([body], { type: "text/plain;charset=UTF-8" }))) return;
    } catch { /* Capture must never interrupt navigation or submission. */ }
    try {
      void window.fetch(config.endpoint, {
        method: "POST", mode: "no-cors", credentials: "omit", referrerPolicy: "no-referrer",
        headers: { "Content-Type": "text/plain;charset=UTF-8" }, body, keepalive: true,
      }).catch(() => {});
    } catch { /* No personal-data diagnostics. */ }
  };

  const poll = async () => {
    if (disposed || completed || !form || reading) return;
    reading = true;
    try {
      const fields = normalizeFields(await form.getFormFieldValues());
      if (disposed || completed) return;
      current = fields;
      report("ready");
      if (!eligiblePartial(fields)) {
        latestEligible = null; queued = null; return;
      }
      const payload = snapshot(fields, "partial");
      latestEligible = payload;
      const nextFingerprint = fingerprint(fields);
      if (sending) {
        queued = nextFingerprint !== attemptedFingerprint ? payload : null;
      } else if (payload && nextFingerprint !== attemptedFingerprint) {
        void send(payload);
      }
    } catch {
      // Do not flush stale PII after the runtime stops providing current state.
      latestEligible = null;
      queued = null;
      report("unavailable");
    } finally {
      reading = false;
    }
  };

  const fromEvent = (event: Event): HubSpotFormInstance | null => {
    const detail = (event as CustomEvent<{ formId?: string; instanceId?: string }>).detail;
    if (detail?.formId !== config.formId || typeof detail.instanceId !== "string") return null;
    const api = (window as Window & { HubSpotFormsV4?: HubSpotV4 }).HubSpotFormsV4;
    if (!api) return null;
    const candidate = api.getFormFromEvent(event);
    if (candidate.getFormId() !== config.formId || candidate.getInstanceId() !== detail.instanceId) return null;
    return candidate;
  };

  const onReady = (event: Event) => {
    try {
      if (disposed || completed) return;
      const candidate = fromEvent(event);
      if (!candidate) return;
      const candidateInstanceId = candidate.getInstanceId();
      const changedInstance = !form || instanceId !== candidateInstanceId;
      form = candidate;
      instanceId = candidateInstanceId;
      if (changedInstance) void poll();
      timer ??= setInterval(() => { void poll(); }, CAPTURE_INTERVAL_MS);
    } catch { report("unavailable"); }
  };

  const onSuccess = (event: Event) => {
    try {
      if (disposed || completed) return;
      const candidate = fromEvent(event);
      if (!candidate) return;
      form = candidate;
      instanceId = candidate.getInstanceId();
      completed = true;
      clearInterval(timer);
      queued = null;
      latestEligible = null;
      let conversionId = "";
      try { conversionId = candidate.getConversionId() || ""; } catch { /* Optional metadata must not lose completion. */ }
      const finish = (fields: CapturedFields) => {
        if (!validEmail(fields.email)) return;
        const payload = snapshot(fields, "completed", conversionId);
        if (payload) beacon(payload);
        // A new logical attempt gets a new ID; existing in-memory completion retains this ID.
        removeSessionStorageItem(sessionKey);
      };
      // Redirect may happen before an async read resolves. Flush the most recent read first.
      if (current) finish(current);
      void candidate.getFormFieldValues().then(raw => {
        if (!disposed) finish(normalizeFields(raw));
      }).catch(() => {});
      report("completed");
    } catch { report("unavailable"); }
  };

  const flush = () => {
    if (!disposed && !completed && latestEligible) beacon(latestEligible);
  };
  const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
  window.addEventListener("hs-form-event:on-ready", onReady);
  window.addEventListener("hs-form-event:on-submission:success", onSuccess);
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", onVisibility);
  report("waiting");

  return () => {
    flush();
    disposed = true;
    clearInterval(timer);
    activeAbort?.abort();
    clearTimeout(sendTimeout);
    queued = null;
    latestEligible = null;
    current = null;
    flushedBody = "";
    lastPayload = null;
    lastPayloadKey = "";
    window.removeEventListener("hs-form-event:on-ready", onReady);
    window.removeEventListener("hs-form-event:on-submission:success", onSuccess);
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
